import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import { FaLock, FaWallet } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface PurchaseModalProps {
  videoId: string;
  price: number;
  onClose: () => void;
  onPurchaseComplete: () => void;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  videoId,
  price,
  onClose,
  onPurchaseComplete
}) => {
  const [userBalance, setUserBalance] = useState(0);
  const [purchasing, setPurchasing] = useState(false);
  const user = getCurrentUser();

  useEffect(() => {
    loadUserBalance();
  }, []);

  const loadUserBalance = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('ponousers')
        .select('wallet_balance')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setUserBalance(data.wallet_balance || 0);
    } catch (err) {
      console.error('Error loading balance:', err);
      toast.error('Failed to load wallet balance');
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please log in to purchase');
      return;
    }

    try {
      setPurchasing(true);

      // Check balance
      if (userBalance < price) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
        return;
      }

      // Update user's balance
      const newBalance = userBalance - price;
      const { error: balanceError } = await supabase
        .from('ponousers')
        .update({ wallet_balance: newBalance })
        .eq('id', user.id);

      if (balanceError) throw balanceError;

      // Record the purchase
      const { error: accessError } = await supabase
        .from('ponovideoaccess')
        .insert({
          video_id: videoId,
          user_id: user.id,
          amount_paid: price
        });

      if (accessError) throw accessError;

      // Record the transaction
      const { error: transactionError } = await supabase
        .from('stripetransactions')
        .insert({
          user_id: user.id,
          amount: price,
          type: 'withdraw',
          status: 'completed',
          description: `Video purchase: ${videoId}`
        });

      if (transactionError) throw transactionError;

      toast.success('Video purchased successfully!');
      onPurchaseComplete();
    } catch (err) {
      console.error('Error purchasing video:', err);
      toast.error('Failed to purchase video');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
        >
          <div className="text-center">
            <FaLock className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Premium Content</h2>
            <p className="text-gray-400 mb-6">
              Purchase this video to unlock full access
            </p>
            
            <div className="bg-black bg-opacity-50 rounded-lg p-4 mb-6">
              <p className="text-3xl font-bold text-red-500 mb-2">
                ${price.toFixed(2)}
              </p>
              <div className="flex items-center justify-center text-gray-400">
                <FaWallet className="w-4 h-4 mr-2" />
                <span>Your Balance: ${userBalance.toFixed(2)}</span>
              </div>
            </div>

            {userBalance < price && (
              <div className="bg-red-900 bg-opacity-50 text-red-200 p-4 rounded-lg mb-6">
                <p>Insufficient balance</p>
                <p className="text-sm">Add funds to your wallet to purchase this video</p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePurchase}
                disabled={purchasing || userBalance < price}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {purchasing ? 'Processing...' : 'Purchase'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PurchaseModal;